import {
  Alert,
  Button,
  Collapse,
  Link,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link as RouterLink } from 'react-router-dom'

import { Box } from '@/components/ui'
import type { StaffDetail, StaffListItem } from '@/definitions/staff'
import { useConfirm } from '@/hooks/useConfirm'
import { useSnackbar } from '@/hooks/useSnackbar'
import { useUpdateStaffProductFlags } from '@/hooks/useUpdateStaffProductFlags'
import { getApiErrorMessage } from '@/utils/apiErrors'

import {
  isStaffIdShaped,
  staffErxConfirmCopy,
  staffErxSuccessMessage,
} from '../../utils/staffErx'

const SAVE_FAILED_MESSAGE = 'E-Prescribe could not be changed for this staff member.'

const PARTIAL_SUCCESS_MESSAGE =
  'Saved, but Dosespot did not apply everything. See the reason below.'

/**
 * Why the switch is locked, and where to go to unlock it.
 *
 * A real anchor rather than a button that navigates: it is a destination inside a
 * sentence, so it should middle-click and open in a new tab like any other link —
 * which matters here, because the operator usually wants the Product Flags tab *and*
 * this staff member, not one instead of the other.
 */
const OrgErxDisabledMessage = ({ orgId }: { orgId: string }) => (
  <>
    This organization does not have E-Prescribe. Its Dosespot clinic has to exist
    before a clinician can be attached to it, so turn E-Prescribe on for the practice
    on the{' '}
    <Link component={RouterLink} to={`/orgs/${orgId}/product-flags`}>
      Product Flags tab
    </Link>{' '}
    first.
  </>
)

const LINK_HELP =
  'Use this only when this person already prescribes at another practice on Barti. ' +
  'Paste the staff id they have there and this organization’s clinic is added to the ' +
  'clinician they already have, instead of a second Dosespot identity being created ' +
  'for the same prescriber.'

const LINK_INVALID_MESSAGE = 'Enter a staff id, or leave this empty.'

interface ErxLinkFormValues {
  erx_link_to_staff_id: string
}

export interface StaffErxSectionProps {
  staff: StaffListItem
  detail: StaffDetail
  orgId: string
  /** False locks the switch: api-service has no clinic to attach a clinician to. */
  isOrgErxEnabled: boolean
  /** True while the staff list is refreshing, which is what the switch follows. */
  isRefreshing: boolean
}

/**
 * One staff member's E-Prescribe flag.
 *
 * The switch reports the stored flag rather than what was last clicked — api-service
 * owns the column and can leave it where it was — so there is nothing staged here and
 * it settles only once the refreshed record arrives.
 *
 * Deliberately reports the flag and nothing else. api-service also keeps an
 * `erx_status` describing the Dosespot clinician, and the two can disagree; which of
 * them decides whether a staff member may prescribe is an open question, so this
 * screen does not interpret either one.
 *
 * Both directions are confirmed, unlike handing a login back on this same card: each
 * one reaches Dosespot, and the dialog is the only place a shared clinician can be
 * shown before it is sent.
 *
 * A failure is kept inline beside the switch rather than in a toast, because the
 * reason is about this staff member and belongs next to the control that caused it.
 */
export const StaffErxSection = ({
  staff,
  detail,
  orgId,
  isOrgErxEnabled,
  isRefreshing,
}: StaffErxSectionProps) => {
  const confirm = useConfirm()
  const { showSuccess, showWarning } = useSnackbar()
  const { mutateAsync, isPending, data, error } = useUpdateStaffProductFlags()

  const [isLinkOpen, setIsLinkOpen] = useState(false)

  // Both read off the mutation rather than mirrored into state. It clears them when the
  // next toggle starts, which is exactly when they stop being true — the same lifecycle
  // a pair of useStates here would have to reproduce by hand.
  const integrationErrors = data?.integration_errors ?? []
  const saveError =
    error === null ? null : getApiErrorMessage(error, SAVE_FAILED_MESSAGE)

  const {
    formState: { errors },
    getValues,
    register,
    reset,
    trigger,
  } = useForm<ErxLinkFormValues>({ defaultValues: { erx_link_to_staff_id: '' } })

  const isErxEnabled = detail.erx_enabled === true
  const isLocked = !isOrgErxEnabled || isPending || isRefreshing

  const handleToggle = async (enabling: boolean) => {
    // A link is only read while a clinician is being created, so a disable ignores
    // whatever was typed rather than being refused for it.
    if (enabling && !(await trigger('erx_link_to_staff_id'))) {
      return
    }

    // Read after validating and before the dialog, so the value echoed in the
    // question is the value sent.
    const typedLink = getValues('erx_link_to_staff_id').trim()
    const linkStaffId = enabling && typedLink !== '' ? typedLink : undefined

    const copy = staffErxConfirmCopy({ staff, enabling, linkStaffId })

    const confirmed = await confirm({
      title: copy.title,
      body: (
        <Box variant='flexColumn' gap='md'>
          <Typography variant='body2'>{copy.effect}</Typography>
          {copy.linkNote !== undefined && (
            <Typography variant='body2' color='text.secondary'>
              {copy.linkNote}
            </Typography>
          )}
        </Box>
      ),
      confirmLabel: copy.confirmLabel,
      destructive: copy.isDestructive,
    })
    if (!confirmed) {
      return
    }

    // A refusal rejects, which is what keeps the reporting below from running; the
    // alert renders it from the mutation's own error state rather than from here.
    const saved = await mutateAsync({
      orgId,
      staffId: staff.id,
      data: {
        erx_enabled: enabling,
        // Omitted rather than sent empty — its presence is what selects the
        // shared-clinician path.
        ...(linkStaffId !== undefined && { erx_link_to_staff_id: linkStaffId }),
      },
    })

    reset()
    setIsLinkOpen(false)
    if (saved.integration_errors.length > 0) {
      // The request itself succeeded, so this is reported as a partial save rather
      // than a failure; what did not apply is listed under the switch.
      showWarning(PARTIAL_SUCCESS_MESSAGE)
    } else {
      // Reported from the stored value, which is not always the one asked for.
      showSuccess(staffErxSuccessMessage(staff, saved.erx_enabled === true))
    }
  }

  return (
    <Box variant='flexColumn' gap='sm'>
      <Typography variant='overline' color='text.secondary'>
        E-Prescribe
      </Typography>

      {/* The same infoRow the Product Flags tab gives a product switch, so a per-staff
          flag reads as the same kind of control as a per-practice one. */}
      <Paper variant='infoRow'>
        <Box variant='flexBetween'>
          <Typography
            variant='subtitle2'
            color={isOrgErxEnabled ? 'text.primary' : 'text.disabled'}
          >
            E-Prescribe (Dosespot)
          </Typography>
          <Switch
            checked={isErxEnabled}
            disabled={isLocked}
            slotProps={{ input: { 'aria-label': 'E-Prescribe (Dosespot)' } }}
            onChange={(event) => void handleToggle(event.target.checked)}
          />
        </Box>
      </Paper>

      {!isOrgErxEnabled && (
        <Alert severity='info'>
          <OrgErxDisabledMessage orgId={orgId} />
        </Alert>
      )}

      {saveError !== null && <Alert severity='error'>{saveError}</Alert>}

      {integrationErrors.map((error) => (
        <Alert key={error.field} severity='warning'>
          {error.message}
        </Alert>
      ))}

      {/* Offered only where it can be read: api-service looks at the link while it
          creates a clinician, so it means nothing once E-Prescribe is already on. */}
      {isOrgErxEnabled && !isErxEnabled && (
        <Box variant='flexColumn' gap='xs'>
          <Box variant='flex' fullWidth={false}>
            <Button
              variant='text'
              size='small'
              aria-expanded={isLinkOpen}
              onClick={() => setIsLinkOpen(!isLinkOpen)}
            >
              {isLinkOpen ? 'Hide clinician link' : 'Link to an existing clinician'}
            </Button>
          </Box>
          <Collapse in={isLinkOpen}>
            <Box variant='flexColumn' gap='sm'>
              <Typography variant='caption' color='text.secondary'>
                {LINK_HELP}
              </Typography>
              <TextField
                label='Staff id at the other practice'
                size='small'
                disabled={isLocked}
                error={errors.erx_link_to_staff_id !== undefined}
                helperText={errors.erx_link_to_staff_id?.message}
                {...register('erx_link_to_staff_id', {
                  validate: (value) =>
                    value.trim() === '' ||
                    isStaffIdShaped(value) ||
                    LINK_INVALID_MESSAGE,
                })}
              />
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  )
}
